'use client'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * A hairline at the bottom of the window that fills as the page is read.
 *
 * Owner: *„może na dole strony głównej pasek przewijania postępu"*.
 *
 * ── WHY IT IS NOT role="progressbar" ─────────────────────────────────────
 * A progress bar reports a TASK running towards completion — an upload, an
 * import, a fuse burning down — and a screen reader announces it as such:
 * something is happening, wait for it. Reading a page is not that. Nothing
 * finishes at 100%, nothing is owed to the person at the end, and scrolling
 * back up is not "progress lost". Announcing "82 percent" while somebody reads
 * would turn a mood signal into an interruption.
 *
 * It is also a duplicate. A screen reader already knows where the cursor sits
 * in the document and says so in its own words, far more usefully than a
 * percentage of pixel height — which counts a tall hero image the same as a
 * paragraph. So the whole rail is `aria-hidden` and carries no role: it exists
 * for the eye that is scrolling, and assistive tech keeps the account it
 * already had.
 *
 * ── WHY THERE IS NO TRACK UNDERNEATH ─────────────────────────────────────
 * An unfilled track would be a 3px line drawn across the bottom of every page,
 * for the entire visit, saying nothing. At the top of the page — when the fill
 * is zero — that line is ALL you would see, and a stray rule under the footer
 * reads as chrome someone forgot to remove rather than as an empty gauge. The
 * fill alone is enough: its leading edge is the whole message, and the browser
 * still draws its own scrollbar for anyone who wants the trough.
 *
 * ── WHY IT DISAPPEARS ON SHORT PAGES ─────────────────────────────────────
 * With nothing to scroll there is no position to report, and a rail stuck at
 * zero would be a broken-looking bar rather than an honest one. It is taken out
 * of the layer entirely instead — and it comes back on its own, because a
 * `ResizeObserver` catches the page growing (a list loading, an accordion
 * opening) when neither `scroll` nor `resize` would fire.
 *
 * ── THE CLOCK, AND WHAT IT DELIBERATELY DOES NOT DO ──────────────────────
 * The scroll listener only asks for a frame; the arithmetic happens once per
 * `requestAnimationFrame`, because scroll events arrive far faster than the
 * screen redraws and reading `scrollHeight` in each one is a forced layout per
 * event. The frame writes `clip-path` straight to the node instead of going
 * through state — sixty React renders a second to move one line would be the
 * expensive way to draw a hairline.
 *
 * `clip-path` rather than `width` or `scaleX()` on purpose: the fill stays the
 * full width of the window, so the brand gradient is always laid out across the
 * viewport and merely uncovered. Scale it and the gradient squashes, which
 * makes the colour under a given point on screen depend on how far the page has
 * been read — the same pixel would be pink at the top and violet at the bottom.
 *
 * There is no transition on the reveal, at any motion setting. The value is
 * repainted every frame from the real scroll position, so a tween would only
 * make the rail lag behind the finger. That is also why `prefers-reduced-motion`
 * needs no branch here: this is a position indicator, not an ornament, so it
 * keeps working — and there is no animation left for the query to switch off.
 */
export function ProgressRail({
  className,
}: {
  /** Merged onto the rail itself — for the rare page that needs it higher. */
  className?: string
}) {
  const fillRef = useRef<HTMLDivElement>(null)
  // Whether the page is long enough to have a position worth reporting — the
  // one thing in here React is allowed to re-render for. It starts `false` so
  // the server and the first client render agree on a hidden rail: nothing on
  // the server knows how tall the document will be, and a rail rendered open
  // and then measured shut is a hairline that flashes on every cold load.
  const [scrollable, setScrollable] = useState(false)

  useEffect(() => {
    const doc = document.documentElement
    let frame = 0

    // `setScrollable` is reached from a frame that runs on every scroll tick,
    // and React only skips the re-render for an unchanged value while the fiber
    // has no other work queued — not something to bet sixty dispatches a second
    // on. The last value is remembered here instead, so React hears about the
    // rail once per change rather than once per frame.
    let visible = false
    const show = (next: boolean) => {
      if (next === visible) return
      visible = next
      setScrollable(next)
    }

    const paint = () => {
      frame = 0
      // `doc.clientHeight`, not `window.innerHeight`: the two differ by the
      // height of a horizontal scrollbar, and `scrollY` stops at
      // `scrollHeight - clientHeight`. Measured against `innerHeight` the span
      // comes out short, so the fill would hit the right edge of the screen a
      // scrollbar's height before the page actually ended. It also keeps both
      // numbers coming from the same box, which is what stops a mobile URL bar
      // sliding away from nudging the fill on its own.
      const span = doc.scrollHeight - doc.clientHeight

      // Sub-pixel spans are rounding noise, not a scrollable page: a document
      // one device pixel taller than the window would otherwise jump between
      // 0% and 100% on a flick.
      if (span <= 1) {
        show(false)
        return
      }
      show(true)

      // Clamped because both ends lie: rubber-band scrolling on iOS reports a
      // negative `scrollY`, and an overscrolled bottom reports more than the
      // span.
      const ratio = Math.min(1, Math.max(0, window.scrollY / span))
      const fill = fillRef.current
      if (fill) fill.style.clipPath = `inset(0 ${(1 - ratio) * 100}% 0 0)`
    }

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(paint)
    }

    // First paint is immediate, not scheduled: the browser restores the old
    // scroll position on a back-navigation, and the rail should already be in
    // the right place before anyone touches the wheel.
    paint()

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule, { passive: true })

    // Content that appears without any event of its own — images settling,
    // a section expanding — changes the denominator, so the box is watched too.
    const observer = new ResizeObserver(schedule)
    observer.observe(document.body)

    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      observer.disconnect()
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [])

  return (
    // `z-50` is chosen against what else is pinned to the bottom edge, not
    // against the page: the order page's mobile action bar and the panel's
    // voice bar are both `fixed bottom-0 z-40`, and at an equal z-index the
    // later element in the DOM wins — so at `z-40` the rail would simply
    // disappear under them on the pages that have one. It still sits below the
    // modals, which declare `z-[90]`, and the toasts at `z-[100]`: a reading
    // indicator must never draw a line across a dialog. `pointer-events-none`
    // on both layers so the rail can sit over a footer link without eating the
    // click.
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-50 h-[3px]',
        // Kept mounted rather than unmounted so the frame that first finds the
        // page scrollable has already written the correct clip to the node.
        scrollable ? 'block' : 'hidden',
        className,
      )}
    >
      <div
        ref={fillRef}
        className="pointer-events-none h-full w-full"
        style={{
          backgroundImage: 'var(--flou-gradient)',
          // Starts fully clipped; the effect's first paint overwrites this
          // within the same frame the rail becomes visible.
          clipPath: 'inset(0 100% 0 0)',
        }}
      />
    </div>
  )
}
